/**
 * Save document persistence. ADR-0007: one versioned document in IndexedDB via
 * `idb-keyval`, read once at boot, written back debounced or on demand. This is
 * the only module that touches IndexedDB (TAD-001) — everything else asks it.
 *
 * `KeyValueStore` exists so this file can be tested at all. Tests run in `node`
 * (`vitest.config.ts`), which has no IndexedDB; a version of this file that
 * called `idb-keyval` directly would be exercised by nothing but a browser.
 * `createSaveStore` takes one as a parameter, defaulting to the real thing, the
 * same shape as `loadLevel`'s injected `fetchImpl` in `levels.ts`.
 */

import { get as idbGet, set as idbSet } from 'idb-keyval';

import type { SaveDocument } from '@contracts/save';
import { SAVE_SCHEMA_VERSION } from '@contracts/save';
import {
  checkSaveSchemaVersion,
  createDefaultSaveDocument,
  describeFaults,
  migrateSaveDocument,
  validateSaveDocument,
} from '@contracts/save-guards';

export interface KeyValueStore {
  get: (key: string) => Promise<unknown>;
  set: (key: string, value: unknown) => Promise<void>;
}

const idbKeyvalStore: KeyValueStore = { get: idbGet, set: idbSet };

/** `PROG-001`: one key for the one document. */
export const SAVE_KEY = 'puzzle-gate:save';

/**
 * `PROG-022`. A save that fails to parse is moved here rather than overwritten,
 * so a player who loses progress to a bug can still get it back if the bug is
 * ever fixed. A second corruption event overwrites whatever this key already
 * held — recovering the *previous* loss is not a promise this makes, only the
 * most recent one.
 */
export const RECOVERY_KEY = 'puzzle-gate:save:unreadable';

export interface LoadNotice {
  kind: 'unreadable' | 'too-new' | 'timeout';
  detail: string;
}

export interface LoadResult {
  document: SaveDocument;
  /** `false` only for `too-new` (`PROG-024`): the document exists but must not be written to. */
  writable: boolean;
  notice: LoadNotice | null;
}

export interface SaveStore {
  /**
   * `PROG-007`, `PROG-020`: reads the store once and caches the result for the
   * life of this instance. Bounded by `loadTimeoutMs` -- a hang here must not
   * leave `Boot.tsx`'s loading state up forever.
   */
  load: () => Promise<LoadResult>;
  /** Writes immediately; resolves only once the write is durable (`PROG-003`). No-ops if the loaded save was `too-new`. */
  save: (document: SaveDocument) => Promise<void>;
  /** Coalesces writes within the window into one (`PROG-009`). */
  scheduleSave: (document: SaveDocument) => void;
  /** Writes a pending scheduled save immediately, e.g. before the session ends (`PROG-004`). No-op if nothing is pending. */
  flush: () => Promise<void>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Default coalescing window. `PROG-009` names 500 ms. */
const DEFAULT_COALESCE_MS = 500;

/**
 * How long boot waits on `store.get` before giving up on it. No requirement
 * names this figure -- TAD-001 records the reasoning.
 */
const DEFAULT_LOAD_TIMEOUT_MS = 3000;

/** Distinguishes "the store answered `undefined`" from "the store never answered". */
const TIMED_OUT = Symbol('save load timed out');

function timeout(ms: number): Promise<typeof TIMED_OUT> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(TIMED_OUT);
    }, ms);
  });
}

export function createSaveStore(
  store: KeyValueStore = idbKeyvalStore,
  coalesceMs = DEFAULT_COALESCE_MS,
  now: () => string = () => new Date().toISOString(),
  loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
): SaveStore {
  let cachedLoad: Promise<LoadResult> | undefined;
  let writable = true;
  let pendingDoc: SaveDocument | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function recoverUnreadable(raw: unknown, detail: string): Promise<LoadResult> {
    try {
      await store.set(RECOVERY_KEY, raw);
    } catch {
      // The store itself has refused a write; PROG-025's retention promise is
      // best-effort once that happens, since there is nowhere left to put the
      // bytes.
    }
    return {
      document: createDefaultSaveDocument(now),
      writable: true,
      notice: { kind: 'unreadable', detail },
    };
  }

  async function readFromStore(): Promise<LoadResult> {
    let raw: unknown;
    try {
      const result = await Promise.race([store.get(SAVE_KEY), timeout(loadTimeoutMs)]);
      if (result === TIMED_OUT) {
        // The abandoned `store.get` is left to resolve or never resolve on its
        // own; nothing is still listening. `writable: false`, the same guard
        // `too-new` uses, matters more here than there: the read that timed
        // out might still land seconds later with the *real* document, and
        // writing a fresh default over it the moment the player does anything
        // would be exactly the silent discard `PROG-025` forbids. A slow read
        // costs this session's progress; it must not cost the previous one's.
        return {
          document: createDefaultSaveDocument(now),
          writable: false,
          notice: {
            kind: 'timeout',
            detail: `save did not load within ${String(loadTimeoutMs)}ms`,
          },
        };
      }
      raw = result;
    } catch {
      // Storage is unavailable (`PROG-027`, manual verification, not this
      // stage). There are no bytes to retain because none arrived, so this is
      // the one unreadable case with nothing to hand `recoverUnreadable`.
      return { document: createDefaultSaveDocument(now), writable: true, notice: null };
    }

    if (raw === undefined) {
      // `PROG-021`.
      return { document: createDefaultSaveDocument(now), writable: true, notice: null };
    }

    if (!isRecord(raw)) {
      return recoverUnreadable(raw, `save is ${typeof raw}, not a document`);
    }

    switch (checkSaveSchemaVersion(raw.schemaVersion)) {
      case 'too-new':
        // `PROG-024`. This build does not know this document's shape and must
        // not guess at it, so play proceeds on an in-memory default rather than
        // an interpretation of data it cannot understand — `writable: false` is
        // what stops that default from ever being written back over the real
        // save.
        return {
          document: createDefaultSaveDocument(now),
          writable: false,
          notice: {
            kind: 'too-new',
            detail: `save was written by a newer version (schema ${String(raw.schemaVersion)})`,
          },
        };

      case 'too-old': {
        // `PROG-023`. `raw.schemaVersion` is a valid integer here, checked by
        // `checkSaveSchemaVersion` immediately above.
        const migrated = migrateSaveDocument(raw, raw.schemaVersion as number);
        if (!migrated.ok) {
          return recoverUnreadable(
            raw,
            `schema ${String(raw.schemaVersion)} has no migration path to ${String(SAVE_SCHEMA_VERSION)}`,
          );
        }
        const validated = validateSaveDocument(migrated.value);
        if (!validated.ok) {
          return recoverUnreadable(
            raw,
            `migrated save is invalid: ${describeFaults(validated.faults)}`,
          );
        }
        // Not persisted here. Reading is not the moment to write; the next
        // `save()` or `scheduleSave()` call carries the migrated shape forward.
        return { document: validated.value, writable: true, notice: null };
      }

      case 'unreadable':
        return recoverUnreadable(raw, 'save does not declare a readable schema version');

      case 'ok': {
        const validated = validateSaveDocument(raw);
        if (!validated.ok) {
          return recoverUnreadable(raw, describeFaults(validated.faults));
        }
        return { document: validated.value, writable: true, notice: null };
      }
    }
  }

  function load(): Promise<LoadResult> {
    cachedLoad ??= readFromStore().then((result) => {
      writable = result.writable;
      return result;
    });
    return cachedLoad;
  }

  async function save(document: SaveDocument): Promise<void> {
    // A pending coalesced write is superseded by this one either way: if this
    // write proceeds it carries newer data, and if it does not (not writable)
    // the pending write must not fire later and overwrite the same document.
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
      pendingDoc = null;
    }

    await load();
    if (!writable) return;

    await store.set(SAVE_KEY, { ...document, updatedAt: now() });
  }

  function scheduleSave(document: SaveDocument): void {
    pendingDoc = document;
    // A window is already open; it will pick up this document when it fires.
    // Resetting the timer on every call would let writes keep arriving forever
    // without ever coalescing into one -- the same throttle-not-debounce choice
    // `platform/announce.ts` makes for polite announcements, and for the same
    // reason.
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      const toWrite = pendingDoc;
      pendingDoc = null;
      if (toWrite) void save(toWrite);
    }, coalesceMs);
  }

  async function flush(): Promise<void> {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
    const toWrite = pendingDoc;
    pendingDoc = null;
    if (toWrite) await save(toWrite);
  }

  return { load, save, scheduleSave, flush };
}

/** The application's one save store. Everything outside this file asks this. */
export const saveStore: SaveStore = createSaveStore();
